import { createMemo } from 'react-use'
import { isUndefined } from 'lodash-es'
import useIsStiffeningPlanPreview from '@editor/pages/PlanView/hooks/useIsStiffeningPlanPreview'
import { isOnVisiblePlanStoreys, usesPlanStoreysOnPlan } from '@editor/utils'
import { useModelStore, useControlStore, roofStoreyKey } from '@editorStores'
import useIsPlanView from './useIsPlanView'

const hiddenElements = (
  model: PlanarModel,
  hiddenElementIds: Set<string>,
  visibilityByType: VisibilityByType,
  visibilityByTypeAndStorey: VisibilityByTypeAndStorey,
  visibleStoreys: Set<string>,
  hiddenElementIdGroups: Record<string, string[]>,
  slabPositionVisibilityFollowsSlabVisibility: boolean,
  roofSlabPositionVisibilityFollowsRoofSlabVisibility: boolean,
  usePlanStoreyMembership: boolean,
  isStiffeningPlanPreview: boolean,
) => {
  const elements = [
    ...model.walls,
    ...model.slabs,
    ...model.roof_slabs,
    ...model.vertical_slabs,
    ...model.rips,
    ...model.lintels,
    ...model.beams,
    ...model.vertical_slabs.flatMap(verticalSlab => (verticalSlab.beam ? [verticalSlab.beam] : [])),
    ...model.vertical_roof_slabs.flatMap(verticalRoofSlab =>
      verticalRoofSlab.beam ? [verticalRoofSlab.beam] : [],
    ),
    ...model.columns,
    ...model.purlins,
    ...model.vertical_roof_slabs,
    ...model.foundationElements,
  ]

  const hiddenElementIdsSet = new Set<string>()

  const hiddenGroupIds = Object.values(hiddenElementIdGroups).flat()

  elements.forEach(element => {
    const guid = 'position_guid' in element ? element.position_guid : element.guid
    const storey = 'storey' in element ? element.storey : undefined
    const type = element.element_type as ElementTypes
    // Evaluate each visibility condition independently

    const isHidden = hiddenElementIds.has(guid) || hiddenGroupIds.includes(guid)

    // Condition 1: Element type visibility and not explicitly hidden
    const isTypeVisible = visibilityByType[type as ElementTypes]

    const usesPlanStorey =
      usePlanStoreyMembership && usesPlanStoreysOnPlan(element, isStiffeningPlanPreview)

    // Condition 2: Storey visibility (simplified visibleStoreys takes precedence).
    // Planansicht uses isInPlanStorey for ceiling/roof positions so UserDefined
    // plan storeys match the exported position plan.
    const isStoreyVisible = usesPlanStorey
      ? isOnVisiblePlanStoreys(element, visibleStoreys)
      : storey
      ? visibleStoreys.has(storey)
      : true

    // Condition 3: Type-specific storey visibility (only checked if storey is visible)
    let isTypeStoreyVisible = true
    if (!usesPlanStorey && storey && storey !== roofStoreyKey && isStoreyVisible) {
      const typeStoreyVisibility = visibilityByTypeAndStorey[type as ElementTypes]
      if (!isUndefined(typeStoreyVisibility)) {
        isTypeStoreyVisible = typeStoreyVisibility.has(storey)
      }
    }

    // Combine all conditions with logical AND
    const isVisible = storey
      ? !isHidden && isTypeVisible && isStoreyVisible && isTypeStoreyVisible
      : !isHidden && isTypeVisible

    if (!isVisible) hiddenElementIdsSet.add(guid)
  })

  if (slabPositionVisibilityFollowsSlabVisibility && !usePlanStoreyMembership) {
    model.vertical_slabs.forEach(verticalSlab => {
      const parentSlabGuid = verticalSlab.beam?.slab_guid
      if (!parentSlabGuid || !hiddenElementIdsSet.has(parentSlabGuid)) return

      hiddenElementIdsSet.add(verticalSlab.guid)
      if (verticalSlab.beam?.guid) hiddenElementIdsSet.add(verticalSlab.beam.guid)
    })
  }

  if (roofSlabPositionVisibilityFollowsRoofSlabVisibility && !usePlanStoreyMembership) {
    model.vertical_roof_slabs.forEach(verticalRoofSlab => {
      const parentRoofSlabGuid = verticalRoofSlab.beam?.slab_guid
      if (!parentRoofSlabGuid || !hiddenElementIdsSet.has(parentRoofSlabGuid)) return

      hiddenElementIdsSet.add(verticalRoofSlab.guid)
      if (verticalRoofSlab.beam?.guid) hiddenElementIdsSet.add(verticalRoofSlab.beam.guid)
    })
  }

  return hiddenElementIdsSet
}

const useMapHiddenElements = createMemo(hiddenElements)

const useHiddenElements = () => {
  const hiddenElementIds = useControlStore(state => state.hiddenElementIds)
  const model = useModelStore(state => state.model)
  const visibilityByType = useModelStore(state => state.visibilityByType)
  const visibilityByTypeAndStorey = useModelStore(state => state.visibilityByTypeAndStorey)
  const visibleStoreys = useModelStore(state => state.visibleStoreys)
  const hiddenElementIdGroups = useControlStore(state => state.hiddenElementIdGroups)
  const slabPositionVisibilityFollowsSlabVisibility = useModelStore(
    state => state.slabPositionVisibilityFollowsSlabVisibility,
  )
  const roofSlabPositionVisibilityFollowsRoofSlabVisibility = useModelStore(
    state => state.roofSlabPositionVisibilityFollowsRoofSlabVisibility,
  )
  const usePlanStoreyMembership = useIsPlanView()
  const isStiffeningPlanPreview = useIsStiffeningPlanPreview()

  return useMapHiddenElements(
    model,
    hiddenElementIds,
    visibilityByType,
    visibilityByTypeAndStorey,
    visibleStoreys,
    hiddenElementIdGroups,
    slabPositionVisibilityFollowsSlabVisibility,
    roofSlabPositionVisibilityFollowsRoofSlabVisibility,
    usePlanStoreyMembership,
    isStiffeningPlanPreview,
  )
}

export default useHiddenElements
